import { useState, MouseEvent, useEffect, useCallback } from 'react';
import { Form, FormRenderProps } from 'react-final-form';
import { connect, useDispatch } from 'react-redux';
import type { Dispatch } from 'redux';

import { Button } from 'UIPackage/MainModule/components/Button';
import { TextInputField } from 'UIPackage/MainModule/components/TextInputField';
import { CheckboxField } from 'UIPackage/MainModule/components/CheckboxField';
import { IKnmDelegateType } from 'APIPackage/CatalogsModule/interfaces/IKnmDelegateType';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { getCatalogKnmDelegateTypes } from 'CorePackage/CatalogsModule/selectors/catalogsSelectors';
import { CommonCatalogsNamesEnum } from 'CorePackage/CatalogsModule/enums/CommonCatalogsNamesEnum';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { Row } from 'UIPackage/FormModule/components/Row';
import { ModalActions } from 'UIPackage/ModalModule/components/ModalActions';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';

import { adminCreateCatalogTypes, adminGetCatalogTypes, adminUpdateCatalogTypes } from '../actions/adminAction';
import styles from './AdminEditForm.module.scss';
import { makeRequired } from '../../../CorePackage/MainModule/validators/isRequired';

type KnmDelegateType = Omit<IKnmDelegateType, 'id'>;

interface IKnmDelegateTypeEditFormProps {
  onBack: () => void;
}

interface IKnmDelegateTypeEditFormStoreProps {
  knmDelegateTypes: IKnmDelegateType[];
}

const KnmDelegateTypeEditFormWithoutStore = ({
  onBack,
  knmDelegateTypes,
}: IKnmDelegateTypeEditFormProps & IKnmDelegateTypeEditFormStoreProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const [selectedKnmDelegateType, setSelectedKnmDelegateType] = useState<IKnmDelegateType | undefined>();
  const [isNew, setIsNew] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);

  const onRowClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      const { id } = event.currentTarget.dataset;
      const knmDelegateType = knmDelegateTypes.find(k => k.id.toString() === id);

      setSelectedKnmDelegateType(prev => (prev?.id === knmDelegateType?.id ? undefined : knmDelegateType));
    },
    [knmDelegateTypes],
  );

  useEffect(() => {
    dispatch(adminGetCatalogTypes(CommonCatalogsNamesEnum.KNM_DELEGATE_TYPES));
  }, [dispatch]);

  const onCreate = useCallback(() => {
    setIsNew(true);
    setSelectedKnmDelegateType({
      id: -1,
      enabled: true,
      code: '',
      name: '',
    });
    setIsFormOpen(true);
  }, []);

  const onEdit = useCallback(() => setIsFormOpen(true), []);

  const onCancel = useCallback(() => {
    setIsNew(false);
    setSelectedKnmDelegateType(undefined);
    setIsFormOpen(false);
  }, []);

  const onSubmit = useCallback(
    (values: KnmDelegateType) => {
      if (isNew) {
        dispatch(adminCreateCatalogTypes({ data: values, catalogName: CommonCatalogsNamesEnum.KNM_DELEGATE_TYPES }));
      } else {
        dispatch(
          adminUpdateCatalogTypes({
            data: { id: selectedKnmDelegateType!.id, ...values },
            catalogName: CommonCatalogsNamesEnum.KNM_DELEGATE_TYPES,
          }),
        );
      }
      onCancel();
    },
    [dispatch, isNew, onCancel, selectedKnmDelegateType],
  );

  const renderForm = useCallback(
    ({ handleSubmit }: FormRenderProps<KnmDelegateType>) => (
      <form onSubmit={handleSubmit}>
        <Row>
          <FieldBlock label="Название" color="black">
            <TextInputField name="name" validate={makeRequired('Поле обязательно')} />
          </FieldBlock>
        </Row>
        <Row>
          <FieldBlock label="Код" color="black">
            <TextInputField name="code" disabled={!isNew} />
          </FieldBlock>
        </Row>
        <Row>
          <CheckboxField id="enabled" name="enabled" label="Активен" />
        </Row>
        <ModalActions placement="left" padding="none">
          <Button color="primary" text="Сохранить" id="saveButton" type="submit" variant="contained" />
          <Button color="default" text="Отмена" variant="contained" onClick={onCancel} />
        </ModalActions>
      </form>
    ),
    [isNew, onCancel],
  );

  if (isFormOpen) {
    return (
      <>
        <Form<KnmDelegateType> onSubmit={onSubmit} initialValues={selectedKnmDelegateType} render={renderForm} />
      </>
    );
  }

  return (
    <div>
      <div className={styles.Table}>
        <span className={styles.Header}>Тип представителя проверяемой организации</span>
        {knmDelegateTypes &&
          knmDelegateTypes.map((knmInspType: IKnmDelegateType) => (
            <button
              key={knmInspType.id}
              className={selectedKnmDelegateType?.id === knmInspType.id ? styles.DataRowSelected : styles.DataRow}
              data-id={knmInspType.id}
              onClick={onRowClick}
              type="button"
            >
              {knmInspType.name}
            </button>
          ))}
      </div>
      <div className={styles.ControlButtons}>
        <Button
          className={styles.Button}
          color="default"
          type="button"
          variant="contained"
          onClick={onBack}
          text="Назад"
        />
        <Button
          className={styles.Button}
          color="primary"
          type="button"
          variant="contained"
          onClick={onCreate}
          text="Создать"
          id="createButton"
        />
        {selectedKnmDelegateType && (
          <Button
            className={styles.Button}
            color="default"
            type="button"
            variant="contained"
            onClick={onEdit}
            text="Редактировать"
          />
        )}
      </div>
    </div>
  );
};

const mapStateToProps = (state: IRootState): IKnmDelegateTypeEditFormStoreProps => ({
  knmDelegateTypes: getCatalogKnmDelegateTypes(state),
});

export const KnmDelegateTypeEditForm = connect(mapStateToProps)(KnmDelegateTypeEditFormWithoutStore);
